import { useEffect } from "react";
import { X, Inbox } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const STATUS_TONE = [["new", "blue"], ["assigned", "teal"], ["not interested", "grey"], ["contacted", "violet"], ["connected", "green"], ["qualified", "amber"],
  ["follow", "orange"], ["meeting", "orange"], ["site visit", "green"], ["negotiation", "purple"], ["book", "green"], ["won", "green"], ["lost", "red"], ["nurture", "blue"],
  ["pending", "orange"], ["completed", "green"], ["active", "green"], ["inactive", "grey"]];
export const toneOf = (s = "") => (STATUS_TONE.find(([k]) => s.toLowerCase().includes(k)) || [, "purple"])[1];
export const StatusBadge = ({ s, testid }) => s ? <span className={`crm-badge tone-${toneOf(s)}`} data-testid={testid}>{s}</span> : <span className="text-[#93A69A]">—</span>;
const PRI = { high: "red", urgent: "red", medium: "orange", low: "grey" };
export const PriorityBadge = ({ p }) => p ? <span className={`crm-badge tone-${PRI[p.toLowerCase()] || "grey"}`}><span className="w-1.5 h-1.5 rounded-full bg-current" />{p}</span> : <span className="text-[#93A69A]">—</span>;

export const activeOpts = (settings, key, current) => (settings?.[key] || []).filter((v) => v === current || !(settings?.inactive?.[key] || []).includes(v));
export const fmtD = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" }) : "—");
export const isOverdue = (d) => !!d && new Date(d) < new Date();
export function timeAgo(d) {
  const s = (Date.now() - new Date(d).getTime()) / 1000, f = Math.abs(s), fut = s < 0;
  const v = f < 60 ? "just now" : f < 3600 ? `${Math.round(f / 60)}m` : f < 86400 ? `${Math.round(f / 3600)}h` : `${Math.round(f / 86400)}d`;
  return v === "just now" ? v : fut ? `in ${v}` : `${v} ago`;
}

export function PageHeader({ title, count, sub, actions, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap mb-5" data-testid={testid}>
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-[#25352C] tracking-tight flex items-baseline gap-2">{title}{count != null && <span className="text-sm font-medium text-[#5F6E64]" data-testid="page-count">{count.toLocaleString("en-IN")}</span>}</h1>
        {sub && <p className="text-[13px] text-[#5F6E64] mt-0.5">{sub}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Card({ title, action, children, className = "", bodyClass = "p-4", testid }) {
  return (
    <section className={`crm-card ${className}`} data-testid={testid}>
      {title && <header className="flex items-center justify-between gap-2 px-4 h-12 border-b border-[#E4EBE4]"><h3 className="text-[14px] font-semibold text-[#25352C]">{title}</h3>{action}</header>}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function Modal({ title, onClose, children, width = "max-w-md", testid }) {
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title} className={`relative bg-white rounded-xl shadow-2xl w-full ${width} max-h-[90vh] flex flex-col crm-fade`} data-testid={testid}>
        <div className="flex items-center justify-between px-5 h-14 border-b border-[#E4EBE4] shrink-0">
          <h3 className="text-[15px] font-semibold text-[#25352C] truncate">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="modal-close" className="crm-btn crm-btn-ghost crm-btn-icon"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export const Field = ({ label, required, hint, children, className = "" }) => (
  <label className={`block ${className}`}><span className="crm-label">{label}{required && <span className="text-[#DC2626]"> *</span>}</span>{children}{hint && <span className="block text-[11px] text-[#5F6E64] mt-1">{hint}</span>}</label>
);

export function EmptyState({ icon: Icon = Inbox, title, text, action, testid = "empty-state" }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6" data-testid={testid}>
      <span className="w-10 h-10 rounded-lg bg-[#F0F7F2] text-[#236B45] flex items-center justify-center"><Icon size={18} /></span>
      <p className="mt-3 text-sm font-semibold text-[#25352C]">{title}</p>
      {text && <p className="mt-1 text-[13px] text-[#5F6E64] max-w-sm">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export const SkeletonRows = ({ rows = 6, cols = 5 }) => (
  <div className="p-4 space-y-3" data-testid="skeleton">{Array.from({ length: rows }).map((_, i) => (
    <div key={i} className="flex gap-4">{Array.from({ length: cols }).map((__, j) => <div key={j} className="crm-skel h-4 flex-1" style={{ maxWidth: j === 0 ? 90 : undefined }} />)}</div>
  ))}</div>
);

export function Tabs({ items, value, onChange, prefix = "tab" }) {
  return (
    <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-[#E4EBE4] mb-4" role="tablist">
      {items.map(([v, l]) => (
        <button key={v} role="tab" aria-selected={value === v} onClick={() => onChange(v)} data-testid={`${prefix}-${v}`}
          className={`px-3 h-10 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${value === v ? "border-[#236B45] text-[#236B45]" : "border-transparent text-[#5F6E64] hover:text-[#25352C]"}`}>{l}</button>
      ))}
    </div>
  );
}

export function Confirm({ open, title, text, confirmLabel = "Confirm", danger, onConfirm, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="confirm-dialog" className="rounded-xl">
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle>{text && <AlertDialogDescription>{text}</AlertDialogDescription>}</AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel" className="crm-btn crm-btn-secondary">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-ok" onClick={onConfirm} className={`crm-btn ${danger ? "!bg-[#B91C1C] hover:!bg-[#991B1B] text-white" : "crm-btn-primary"}`}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const Avatar = ({ name = "?", size = 28 }) => (
  <span className="rounded-full bg-[#F0F7F2] text-[#236B45] font-semibold flex items-center justify-center shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>{name.replace(/\[.*?\]\s*/, "")[0]?.toUpperCase()}</span>
);
